import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  Eye,
  FileBarChart,
  Layers,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FinanceLockGate from '../components/FinanceLockGate';
import useAuthStore from '../store/useAuthStore';
import { hasFinanceRole, isOwnerAdminRole } from '../utils/rolePermissions';
import {
  generateAllMonthlyReports,
  generateMonthlyReport,
  getMonthlyReportById,
  getMonthlyReportCurrentStatus,
  getMonthlyReportTypes,
  listMonthlyReports,
  regenerateMonthlyReport,
} from '../services/monthlyReportsApi';

const RUPEE = '\u20B9';

const REPORT_TYPE_LABELS = Object.freeze({
  progress: 'Monthly Progress Report',
  pnl: 'Monthly P&L Statement',
  consumption: 'Monthly Consumption Report',
  consultant_fees: 'Monthly Consultant Fees Report',
  staff_work_timings: 'Monthly Staff Work Timings Report',
  attendance: 'Monthly Attendance Report',
  emi_collection: 'Monthly EMI and Collection Report',
  inventory_restock: 'Monthly Inventory Restock Report',
});

const STATUS_LABELS = Object.freeze({
  generated: 'Generated',
  failed: 'Failed',
  locked: 'Locked',
  superseded: 'Superseded',
  draft: 'Draft',
  not_generated: 'Not Generated',
  partial: 'Partial',
  ready: 'Ready',
});

function getCurrentMonthYear() {
  const now = new Date();
  return {
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  };
}

function formatCurrency(value) {
  return `${RUPEE}${Number(value || 0).toFixed(2)}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-IN');
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function formatDateTime(dateValue) {
  if (!dateValue) return '-';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(parsed);
}

function labelFromKey(value, fallback = '-') {
  if (!value) return fallback;
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function typeLabel(type) {
  return REPORT_TYPE_LABELS[type] || labelFromKey(type);
}

function statusLabel(status) {
  return STATUS_LABELS[status] || labelFromKey(status);
}

function getSourceStatusMeta(sourceStatus) {
  if (sourceStatus === 'complete') {
    return {
      badge: 'pill-status pill-green',
      text: 'Complete',
    };
  }
  if (sourceStatus === 'partial') {
    return {
      badge: 'pill-status pill-yellow',
      text: 'Partial',
    };
  }
  return {
    badge: 'pill-status pill-red',
    text: sourceStatus === 'unavailable' ? 'Unavailable' : labelFromKey(sourceStatus, 'Unavailable'),
  };
}

function getStatusPill(status) {
  if (status === 'generated' || status === 'locked') return 'pill-status pill-green';
  if (status === 'superseded') return 'pill-status pill-yellow';
  if (status === 'failed') return 'pill-status pill-red';
  return 'pill-status bg-slate-100 text-slate-700';
}

function getFriendlyError(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const backendMessage = error?.response?.data?.error || error?.message || 'Request failed.';

  if (status === 423 || code === 'FINANCE_LOCK_REQUIRED') {
    return 'Finance Lock is enabled. Unlock finance access and try again.';
  }
  if (status === 403) {
    return backendMessage || 'You do not have permission for this action.';
  }
  if (status === 409 || code === 'MONTHLY_REPORT_EXISTS') {
    return backendMessage || 'Monthly report already exists for this month and type.';
  }
  if (status === 400) {
    return backendMessage || 'Please check inputs and try again.';
  }

  return backendMessage;
}

function SourceStatusBadge({ sourceStatus }) {
  const meta = getSourceStatusMeta(sourceStatus);
  return <span className={meta.badge}>{meta.text}</span>;
}

function MetricCard({ label, value }) {
  return (
    <div className="metric-card">
      <p className="metric-label">{label}</p>
      <p className="metric-value">{value}</p>
    </div>
  );
}

function SnapshotTable({ columns, rows, emptyText = 'No rows available.' }) {
  return (
    <div className="table-shell">
      <div className="overflow-x-auto">
        <table className="table-modern">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!rows?.length ? (
              <tr>
                <td colSpan={columns.length}>{emptyText}</td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr key={row.id || `${rowIndex}-${columns[0].key}`}>
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render ? column.render(row) : row[column.key] ?? '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportSnapshotView({ report }) {
  const snapshot = report?.snapshotJson || {};
  const summary = report?.summaryJson || {};
  const sourceMeta = getSourceStatusMeta(snapshot.sourceStatus);

  const renderProgressReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Appointments" value={formatNumber(summary.appointmentsCount)} />
        <MetricCard label="New Patients" value={formatNumber(summary.newPatients)} />
        <MetricCard label="Completed Cases" value={formatNumber(summary.completedCases)} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Treatments" value={formatNumber(summary.treatmentCount)} />
        <MetricCard label="Revenue" value={formatCurrency(summary.revenue)} />
        <MetricCard label="Chair Utilization" value={formatPercent(summary.chairUtilizationPercent)} />
      </div>
      <div className="section-card-soft p-4">
        <p className="font-semibold text-slate-900">Revenue Summary</p>
        <p className="mt-2 text-sm text-slate-600">
          Billed Revenue: <span className="font-semibold text-slate-900">{formatCurrency(snapshot?.revenueSummary?.billedRevenue)}</span>
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Invoice Count: <span className="font-semibold text-slate-900">{formatNumber(snapshot?.revenueSummary?.invoiceCount)}</span>
        </p>
      </div>
    </div>
  );

  const renderPnlReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Revenue" value={formatCurrency(summary.revenue)} />
        <MetricCard label="Gross Profit" value={formatCurrency(summary.grossProfit)} />
        <MetricCard label="Net Profit" value={formatCurrency(summary.netProfit)} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Operating Expenses" value={formatCurrency(summary.operatingExpenses)} />
        <MetricCard label="EBITDA" value={formatCurrency(summary.ebitda)} />
        <MetricCard label="ROI" value={formatPercent(summary.roi)} />
      </div>
      <SnapshotTable
        columns={[
          { key: 'label', label: 'Metric' },
          { key: 'value', label: 'Value' },
        ]}
        rows={[
          { id: 'actualVariableCosts', label: 'Actual Variable Costs', value: formatCurrency(summary.actualVariableCosts) },
          { id: 'labCost', label: 'Lab Cost', value: formatCurrency(summary.labCost) },
          { id: 'consultantFees', label: 'Consultant Fees', value: formatCurrency(summary.consultantFees) },
          { id: 'depreciation', label: 'Depreciation', value: formatCurrency(summary.depreciation) },
          { id: 'interest', label: 'Interest', value: formatCurrency(summary.interest) },
          { id: 'ebit', label: 'EBIT', value: formatCurrency(summary.ebit) },
          { id: 'tax', label: 'Tax', value: formatCurrency(summary.tax) },
        ]}
      />
      {snapshot?.consultantFeeSource || snapshot?.doubleCountingHandling ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-semibold">Consultant Fee Source</p>
          <p className="mt-1">Source: {labelFromKey(snapshot.consultantFeeSource, '-')}</p>
          {snapshot.doubleCountingHandling ? <p className="mt-1">{snapshot.doubleCountingHandling}</p> : null}
        </div>
      ) : null}
    </div>
  );

  const renderConsumptionReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <MetricCard label="Total Consumable Cost" value={formatCurrency(summary.totalActualConsumableCost)} />
        <MetricCard label="Finalized Audit Rows" value={formatNumber(summary.finalizedAuditRows)} />
      </div>
      <SnapshotTable
        columns={[
          { key: 'consumableName', label: 'Consumable' },
          { key: 'quantityUsed', label: 'Quantity Used', render: (row) => formatNumber(row.quantityUsed) },
          { key: 'actualCost', label: 'Actual Cost', render: (row) => formatCurrency(row.actualCost) },
        ]}
        rows={snapshot?.topConsumedItems || []}
        emptyText="No top consumed item rows available."
      />
    </div>
  );

  const renderConsultantFeesReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Total Payable" value={formatCurrency(summary.totalPayable)} />
        <MetricCard label="Total Paid" value={formatCurrency(summary.totalPaid)} />
        <MetricCard label="Total Balance" value={formatCurrency(summary.totalBalance)} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <MetricCard label="Pending Records" value={formatNumber(summary.pendingCount)} />
        <MetricCard label="Fully Paid Records" value={formatNumber(summary.fullyPaidCount)} />
      </div>
      <SnapshotTable
        columns={[
          { key: 'consultantName', label: 'Consultant' },
          { key: 'totalAgreedFee', label: 'Agreed Fee', render: (row) => formatCurrency(row.totalAgreedFee) },
          { key: 'totalPaid', label: 'Paid', render: (row) => formatCurrency(row.totalPaid) },
          { key: 'balanceRemaining', label: 'Balance', render: (row) => formatCurrency(row.balanceRemaining) },
        ]}
        rows={snapshot?.consultantWiseTotals || []}
        emptyText="No consultant-wise rows available."
      />
    </div>
  );

  const renderStaffWorkTimingReport = () => (
    <div className="space-y-4">
      <SnapshotTable
        columns={[
          { key: 'staffName', label: 'Staff' },
          { key: 'totalWorkedMinutes', label: 'Worked Minutes', render: (row) => formatNumber(row.totalWorkedMinutes) },
          { key: 'totalLateMinutes', label: 'Late Minutes', render: (row) => formatNumber(row.totalLateMinutes) },
          { key: 'totalOvertimeMinutes', label: 'Overtime Minutes', render: (row) => formatNumber(row.totalOvertimeMinutes) },
          { key: 'missingCheckoutCount', label: 'Missing Checkout', render: (row) => formatNumber(row.missingCheckoutCount) },
        ]}
        rows={snapshot?.staffWorkTimings || []}
        emptyText="No staff work timing rows available."
      />
    </div>
  );

  const renderAttendanceReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Present Days" value={formatNumber(summary.totalPresentDays)} />
        <MetricCard label="Absent Days" value={formatNumber(summary.totalAbsentDays)} />
        <MetricCard label="Leave Days" value={formatNumber(summary.totalLeaveDays)} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Half-days" value={formatNumber(summary.totalHalfDays)} />
        <MetricCard label="Holidays" value={formatNumber(summary.totalHolidays)} />
        <MetricCard label="Corrections" value={formatNumber(summary.correctionsCount)} />
      </div>
      <SnapshotTable
        columns={[
          { key: 'staffName', label: 'Staff' },
          { key: 'presentDays', label: 'Present', render: (row) => formatNumber(row.presentDays) },
          { key: 'absentDays', label: 'Absent', render: (row) => formatNumber(row.absentDays) },
          { key: 'leaveDays', label: 'Leave', render: (row) => formatNumber(row.leaveDays) },
          { key: 'halfDays', label: 'Half-day', render: (row) => formatNumber(row.halfDays) },
          { key: 'holidays', label: 'Holiday', render: (row) => formatNumber(row.holidays) },
        ]}
        rows={snapshot?.staffWiseAttendance || []}
        emptyText="No staff attendance rows available."
      />
    </div>
  );

  const renderEmiCollectionReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Billed Amount" value={formatCurrency(summary.billedAmount)} />
        <MetricCard label="Collected Amount" value={formatCurrency(summary.collectedAmount)} />
        <MetricCard label="Pending Amount" value={formatCurrency(summary.pendingAmount)} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="EMI Due" value={formatCurrency(summary.emiDueAmount)} />
        <MetricCard label="EMI Paid" value={formatCurrency(summary.emiPaidAmount)} />
        <MetricCard label="Overdue EMI" value={formatCurrency(summary.overdueEmiAmount)} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <MetricCard label="Collection Rate" value={formatPercent(summary.collectionRate)} />
        <MetricCard label="EMI Exposure" value={formatPercent(summary.emiExposure)} />
      </div>
    </div>
  );

  const renderInventoryRestockReport = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="Low Stock Items" value={formatNumber(summary.lowStockCount)} />
        <MetricCard label="Critical Items" value={formatNumber(summary.criticalCount)} />
        <MetricCard label="Restock Additions" value={formatNumber(summary.restockAdditions)} />
      </div>
      <SnapshotTable
        columns={[
          { key: 'consumableId', label: 'Consumable ID' },
          { key: 'currentStockQty', label: 'Current Stock', render: (row) => formatNumber(row.currentStockQty) },
          { key: 'restockQty', label: 'Restock Qty', render: (row) => formatNumber(row.restockQty) },
          { key: 'stockStatus', label: 'Status', render: (row) => labelFromKey(row.stockStatus) },
        ]}
        rows={snapshot?.lowStockItems || []}
        emptyText="No low stock rows available."
      />
      <SnapshotTable
        columns={[
          { key: 'movementType', label: 'Movement Type', render: (row) => labelFromKey(row.movementType) },
          { key: 'count', label: 'Count', render: (row) => formatNumber(row.count) },
          { key: 'quantity', label: 'Quantity', render: (row) => formatNumber(row.quantity) },
          { key: 'totalValue', label: 'Total Value', render: (row) => formatCurrency(row.totalValue) },
        ]}
        rows={snapshot?.stockMovementSummary || []}
        emptyText="No stock movement summary rows available."
      />
    </div>
  );

  const renderByType = () => {
    if (!report?.reportType) {
      return <p className="text-sm text-slate-500">Report type unavailable.</p>;
    }

    if (report.reportType === 'progress') return renderProgressReport();
    if (report.reportType === 'pnl') return renderPnlReport();
    if (report.reportType === 'consumption') return renderConsumptionReport();
    if (report.reportType === 'consultant_fees') return renderConsultantFeesReport();
    if (report.reportType === 'staff_work_timings') return renderStaffWorkTimingReport();
    if (report.reportType === 'attendance') return renderAttendanceReport();
    if (report.reportType === 'emi_collection') return renderEmiCollectionReport();
    if (report.reportType === 'inventory_restock') return renderInventoryRestockReport();

    return <p className="text-sm text-slate-500">No preview renderer found for this report type.</p>;
  };

  return (
    <div className="space-y-5">
      <section className="section-card-soft p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Report Type</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{typeLabel(report.reportType)}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Period</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {report.reportMonth}/{report.reportYear}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Version</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">v{report.version || 1}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Status</p>
            <p className="mt-1">
              <span className={getStatusPill(report.status)}>{statusLabel(report.status)}</span>
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Generated At</p>
            <p className="mt-1 text-sm text-slate-700">{formatDateTime(report.generatedAt)}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Source Status</p>
            <p className="mt-1">
              <span className={sourceMeta.badge}>{sourceMeta.text}</span>
            </p>
          </div>
        </div>
      </section>

      {Array.isArray(snapshot?.missingSources) && snapshot.missingSources.length > 0 ? (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Missing Sources</p>
          <ul className="mt-2 list-disc pl-5">
            {snapshot.missingSources.map((source, index) => (
              <li key={`${source.source || 'unknown'}-${index}`}>
                {labelFromKey(source.source, 'Unknown source')}
                {source.error ? `: ${source.error}` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="section-card p-5">{renderByType()}</section>
    </div>
  );
}

export default function MonthlyReports() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const canUseFinance = hasFinanceRole(user?.role);
  const canGenerate = isOwnerAdminRole(user?.role);
  const current = getCurrentMonthYear();

  const [typesLoading, setTypesLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [statusCard, setStatusCard] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generationLoading, setGenerationLoading] = useState('');
  const [regenerationReason, setRegenerationReason] = useState('');

  const [filters, setFilters] = useState({
    month: String(current.month),
    year: String(current.year),
    reportType: '',
    status: '',
    includeSuperseded: true,
  });
  const [generateType, setGenerateType] = useState('');

  const financeTypesMap = useMemo(() => {
    const map = new Map();
    (types || []).forEach((row) => map.set(row.reportType, Boolean(row.financeSensitive)));
    return map;
  }, [types]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: String(index + 1),
        label: String(index + 1),
      })),
    []
  );

  const yearOptions = useMemo(() => {
    const thisYear = new Date().getFullYear();
    return [thisYear - 1, thisYear, thisYear + 1].map((year) => String(year));
  }, []);

  const loadTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const payload = await getMonthlyReportTypes();
      const rowsData = Array.isArray(payload?.reportTypes) ? payload.reportTypes : [];
      setTypes(rowsData);
      if (!generateType && rowsData[0]?.reportType) {
        setGenerateType(rowsData[0].reportType);
      }
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setTypesLoading(false);
    }
  }, [generateType]);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const payload = await getMonthlyReportCurrentStatus({
        month: Number(filters.month),
        year: Number(filters.year),
      });
      setStatusCard(payload || null);
    } catch (error) {
      setStatusCard(null);
      toast.error(getFriendlyError(error));
    } finally {
      setLoadingStatus(false);
    }
  }, [filters.month, filters.year]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const payload = await listMonthlyReports({
        month: Number(filters.month),
        year: Number(filters.year),
        reportType: filters.reportType || undefined,
        status: filters.status || undefined,
        includeSuperseded: filters.includeSuperseded,
        limit: 100,
        offset: 0,
      });
      setRows(Array.isArray(payload?.rows) ? payload.rows : []);
      setRowsCount(Number(payload?.count || 0));
    } catch (error) {
      setRows([]);
      setRowsCount(0);
      toast.error(getFriendlyError(error));
    } finally {
      setLoadingRows(false);
    }
  }, [filters.includeSuperseded, filters.month, filters.reportType, filters.status, filters.year]);

  const loadAll = useCallback(async () => {
    await Promise.all([loadTypes(), loadStatus(), loadRows()]);
  }, [loadRows, loadStatus, loadTypes]);

  const refreshListAndStatus = async () => {
    await Promise.all([loadStatus(), loadRows()]);
  };

  const openReport = async (reportId) => {
    setDetailLoading(true);
    setActiveModal('detail');
    try {
      const payload = await getMonthlyReportById(reportId);
      setSelectedReport(payload || null);
    } catch (error) {
      setSelectedReport(null);
      setActiveModal(null);
      toast.error(getFriendlyError(error));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGenerateSingle = async () => {
    if (!canGenerate) {
      toast.error('Only Owner/Admin can generate monthly reports.');
      return;
    }
    if (!generateType) {
      toast.error('Select a report type first.');
      return;
    }

    setGenerationLoading('single');
    try {
      const payload = await generateMonthlyReport({
        month: Number(filters.month),
        year: Number(filters.year),
        reportType: generateType,
      });
      toast.success(`Generated ${payload?.count || 1} report.`);
      await refreshListAndStatus();
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setGenerationLoading('');
    }
  };

  const handleGenerateAll = async () => {
    if (!canGenerate) {
      toast.error('Only Owner/Admin can generate all monthly reports.');
      return;
    }
    setGenerationLoading('all');
    try {
      const payload = await generateAllMonthlyReports({
        month: Number(filters.month),
        year: Number(filters.year),
      });
      toast.success(`Generated ${payload?.count || 0} monthly reports.`);
      await refreshListAndStatus();
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setGenerationLoading('');
    }
  };

  const openRegenerate = (report) => {
    setSelectedReport(report);
    setRegenerationReason('');
    setActiveModal('regenerate');
  };

  const handleRegenerate = async () => {
    if (!canGenerate) {
      toast.error('Only Owner/Admin can regenerate reports.');
      return;
    }
    if (!selectedReport?.id) {
      toast.error('Select a report first.');
      return;
    }
    if (!String(regenerationReason || '').trim()) {
      toast.error('Regeneration reason is required.');
      return;
    }

    setGenerationLoading('regenerate');
    try {
      await regenerateMonthlyReport(selectedReport.id, {
        generationReason: regenerationReason.trim(),
      });
      toast.success('Regeneration completed with new version.');
      setActiveModal(null);
      setRegenerationReason('');
      await refreshListAndStatus();
    } catch (error) {
      toast.error(getFriendlyError(error));
    } finally {
      setGenerationLoading('');
    }
  };

  if (!canUseFinance) {
    return (
      <div className="app-page">
        <div className="section-card p-6">
          <h1 className="page-title">Monthly Cloud Reports</h1>
          <p className="mt-2 text-sm text-amber-700">
            Your role does not have finance report access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <FinanceLockGate
      title="Finance Lock enabled. Enter PIN to continue."
      description="Monthly Cloud Reports contain finance, payment, EMI, consultant, and inventory value data."
      onAccessGranted={loadAll}
    >
      {(typesLoading && !types.length) || loadingRows ? (
        <div className="section-card p-6 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
            Loading Monthly Cloud Reports...
          </div>
        </div>
      ) : (
        <div className="app-page space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="page-title">Monthly Cloud Reports</h1>
              <p className="page-subtitle">
                View saved monthly business, finance, inventory, consultant, EMI, and attendance reports.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-secondary" onClick={() => navigate('/reports')}>
                <FileBarChart className="h-4 w-4" />
                Business Reports
              </button>
              {canGenerate ? (
                <>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleGenerateSingle}
                    disabled={generationLoading === 'single'}
                  >
                    {generationLoading === 'single' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Generate Report
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleGenerateAll}
                    disabled={generationLoading === 'all'}
                  >
                    {generationLoading === 'all' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Layers className="h-4 w-4" />
                    )}
                    Generate All Reports
                  </button>
                </>
              ) : null}
            </div>
          </div>

          <section className="section-card p-5 space-y-4">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-6">
              <div>
                <label className="label-modern">Month</label>
                <select
                  className="input-modern"
                  value={filters.month}
                  onChange={(event) => setFilters((prev) => ({ ...prev, month: event.target.value }))}
                >
                  {monthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-modern">Year</label>
                <select
                  className="input-modern"
                  value={filters.year}
                  onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-modern">Report Type</label>
                <select
                  className="input-modern"
                  value={filters.reportType}
                  onChange={(event) => setFilters((prev) => ({ ...prev, reportType: event.target.value }))}
                >
                  <option value="">All Types</option>
                  {types.map((type) => (
                    <option key={type.reportType} value={type.reportType}>
                      {typeLabel(type.reportType)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-modern">Status</label>
                <select
                  className="input-modern"
                  value={filters.status}
                  onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                >
                  <option value="">All</option>
                  <option value="generated">Generated</option>
                  <option value="locked">Locked</option>
                  <option value="failed">Failed</option>
                  <option value="superseded">Superseded</option>
                </select>
              </div>
              <div>
                <label className="label-modern">Generate Type</label>
                <select
                  className="input-modern"
                  value={generateType}
                  onChange={(event) => setGenerateType(event.target.value)}
                  disabled={!canGenerate}
                >
                  {types.map((type) => (
                    <option key={`generate-${type.reportType}`} value={type.reportType}>
                      {typeLabel(type.reportType)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button type="button" className="btn-secondary w-full" onClick={refreshListAndStatus}>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="include-superseded"
                type="checkbox"
                checked={filters.includeSuperseded}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, includeSuperseded: event.target.checked }))
                }
              />
              <label htmlFor="include-superseded" className="text-sm text-slate-600">
                Include superseded versions
              </label>
            </div>
          </section>

          <section className="section-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Current Month Status</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Month {filters.month}/{filters.year}
                </p>
              </div>
              {loadingStatus ? <Loader2 className="h-4 w-4 animate-spin text-indigo-600" /> : null}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
              <MetricCard label="Overall Status" value={statusLabel(statusCard?.status || 'not_generated')} />
              <MetricCard label="Generated Types" value={formatNumber(statusCard?.generatedCount)} />
              <MetricCard label="Failed Types" value={formatNumber(statusCard?.failedCount)} />
              <MetricCard label="Total Types" value={formatNumber(statusCard?.totalTypes || types.length)} />
            </div>
          </section>

          <section className="section-card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Saved Reports ({rowsCount})</h2>
              <div className="text-sm text-slate-500">Snapshot-based immutable monthly records</div>
            </div>

            <div className="mt-4 table-shell">
              <div className="overflow-x-auto">
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>Report Type</th>
                      <th>Month/Year</th>
                      <th>Status</th>
                      <th>Version</th>
                      <th>Generated At</th>
                      <th>Generated By</th>
                      <th>Source Status</th>
                      <th>Finance Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!rows.length ? (
                      <tr>
                        <td colSpan={9}>No monthly reports found for selected filters.</td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.id}>
                          <td className="font-semibold text-slate-900">{typeLabel(row.reportType)}</td>
                          <td>
                            {row.reportMonth}/{row.reportYear}
                          </td>
                          <td>
                            <span className={getStatusPill(row.status)}>{statusLabel(row.status)}</span>
                            {row.status === 'superseded' ? (
                              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                Superseded
                              </span>
                            ) : null}
                          </td>
                          <td>v{row.version || 1}</td>
                          <td>{formatDateTime(row.generatedAt)}</td>
                          <td>{row?.Generator?.name || row?.Generator?.email || '-'}</td>
                          <td>
                            <SourceStatusBadge sourceStatus={row?.snapshotJson?.sourceStatus || 'unavailable'} />
                          </td>
                          <td>
                            {financeTypesMap.get(row.reportType) ? (
                              <span className="pill-status pill-yellow">Finance</span>
                            ) : (
                              <span className="pill-status bg-slate-100 text-slate-700">Operational</span>
                            )}
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn-secondary px-3 py-2 text-xs"
                                onClick={() => openReport(row.id)}
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View
                              </button>
                              {canGenerate ? (
                                <button
                                  type="button"
                                  className="btn-secondary px-3 py-2 text-xs"
                                  onClick={() => openRegenerate(row)}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Regenerate
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {activeModal === 'detail' ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
              <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.28)]">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                  <h3 className="text-lg font-semibold text-slate-900">Monthly Report Detail</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveModal(null);
                      setSelectedReport(null);
                    }}
                    className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-[80vh] overflow-y-auto p-5">
                  {detailLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                      Loading report snapshot...
                    </div>
                  ) : selectedReport ? (
                    <ReportSnapshotView report={selectedReport} />
                  ) : (
                    <p className="text-sm text-slate-500">Report data not available.</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {activeModal === 'regenerate' ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
              <div className="w-full max-w-xl rounded-[24px] border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.28)]">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                  <h3 className="text-lg font-semibold text-slate-900">Regenerate Monthly Report</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveModal(null);
                      setSelectedReport(null);
                      setRegenerationReason('');
                    }}
                    className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-4 p-5">
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <p className="font-semibold">
                          Regeneration creates a new version and does not overwrite the old report.
                        </p>
                        <p className="mt-1">
                          Report: {typeLabel(selectedReport?.reportType)} | Period: {selectedReport?.reportMonth}/{selectedReport?.reportYear}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="label-modern">Regeneration Reason *</label>
                    <textarea
                      className="textarea-modern"
                      value={regenerationReason}
                      onChange={(event) => setRegenerationReason(event.target.value)}
                      placeholder="Explain why regeneration is required"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setActiveModal(null);
                        setSelectedReport(null);
                        setRegenerationReason('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleRegenerate}
                      disabled={generationLoading === 'regenerate'}
                    >
                      {generationLoading === 'regenerate' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Regenerate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <section className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
            <div className="flex items-start gap-2">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Scheduler auto-generation is not enabled in this step. Reports here are manually generated snapshots.
              </p>
            </div>
          </section>

          {!canGenerate ? (
            <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Your role can view finance reports after Finance Lock unlock, but only Owner/Admin can generate or regenerate reports.
                </p>
              </div>
            </section>
          ) : null}
        </div>
      )}
    </FinanceLockGate>
  );
}
